import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Film, Trash2, Loader2, RefreshCw, Ghost, Lock } from "lucide-react";
import { toast } from "sonner";
import { usePager, PagerBar } from "@/components/admin/usePager";

export const fmtBytes = (b) => {
  b = Number(b || 0);
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} Go`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} Mo`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(0)} Ko`;
  return `${b} o`;
};
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const fileName = (path) => (path || "").split("/").pop();

const Stat = ({ label, value, tone }) => (
  <div className="rounded-2xl border p-4" style={{ borderColor: "var(--yalola-border)", background: "#fff" }}>
    <div className="text-2xl font-extrabold" style={{ color: tone || "var(--yalola-text)" }}>{value}</div>
    <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
  </div>
);

export const R2StorageSection = () => {
  const [data, setData] = useState(null);
  const [orphans, setOrphans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    setSel(new Set());
    const [v, o] = await Promise.allSettled([api.get("/admin/r2/videos"), api.get("/admin/r2/orphans")]);
    if (v.status === "fulfilled") setData(v.value.data); else toast.error("Impossible de charger les vidéos R2");
    if (o.status === "fulfilled") setOrphans(o.value.data); else toast.error("Impossible de charger les envois abandonnés");
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const videos = data?.videos || [];
  const summary = data?.summary || {};
  const selectable = useMemo(() => videos.filter((v) => !v.referenced), [videos]);
  const pager = usePager(videos);
  const oSum = orphans?.summary || {};

  const toggle = (p) => setSel((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });

  const runCleanup = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/r2/videos/cleanup", { paths: [...sel] });
      toast.success(`${r.deleted} fichier(s) supprimé(s)${r.skipped_referenced ? ` · ${r.skipped_referenced} protégé(s)` : ""}`);
      setConfirm(false);
      await load();
    } catch {
      toast.error("Échec de la suppression");
    } finally {
      setBusy(false);
    }
  };

  const runOrphanPurge = async () => {
    setPurging(true);
    try {
      const { data: r } = await api.post("/admin/r2/orphans/purge", {});
      toast.success(`${r.aborted} envoi(s) abandonné(s) purgé(s) · ${fmtBytes(r.bytes_freed)} libérés${r.skipped_recent ? ` · ${r.skipped_recent} récent(s) conservé(s)` : ""}`);
      await load();
    } catch {
      toast.error("Échec de la purge");
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-r2-section">
      <div className="flex items-start justify-between gap-3 flex-wrap pt-2 border-t" style={{ borderColor: "var(--yalola-border)" }}>
        <div>
          <h3 className="text-base font-bold flex items-center gap-2 mt-3"><Film size={18} style={{ color: "#0369A1" }} /> Vidéos du fil & Reels (Cloudflare R2)</h3>
          <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>
            Fichiers vidéo importés par les utilisateurs (fil, Reels, stories, messages). Les fichiers <strong>utilisés</strong> par une publication sont protégés.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading || busy} data-testid="r2-refresh-btn">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Fichiers vidéo" value={summary.count ?? "—"} />
        <Stat label="Espace occupé" value={summary.bytes != null ? fmtBytes(summary.bytes) : "—"} />
        <Stat label="Non utilisées" value={summary.unused ?? "—"} tone="#B45309" />
        <Stat label="Envois abandonnés" value={oSum.count != null ? `${oSum.count} · ${fmtBytes(oSum.bytes)}` : "—"} tone="#DC2626" />
      </div>

      {/* Abandoned multipart uploads (invisible orphan chunks) */}
      <div className="rounded-2xl border p-3 flex items-start gap-3 flex-wrap" style={{ borderColor: "var(--yalola-border)", background: "#FFF7ED" }} data-testid="r2-orphans-card">
        <Ghost size={16} className="shrink-0 mt-0.5" style={{ color: "#C2410C" }} />
        <div className="flex-1 min-w-0 text-xs" style={{ color: "#9A3412" }}>
          <strong>Envois abandonnés (orphelins)&nbsp;:</strong> morceaux de vidéos dont l'importation a été annulée ou interrompue brutalement. Invisibles partout mais <strong>facturés</strong> par Cloudflare.
          {oSum.count != null && <> Actuellement&nbsp;: <strong>{oSum.count}</strong> envoi(s) · <strong>{fmtBytes(oSum.bytes)}</strong>.</>}
          {" "}La purge ignore les envois de moins de 6&nbsp;h (importations peut-être en cours).
        </div>
        <Button variant="outline" onClick={runOrphanPurge} disabled={purging || loading || !oSum.count} data-testid="r2-orphans-purge-btn">
          {purging ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Purger les envois abandonnés ({oSum.count || 0})
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          disabled={busy || loading || sel.size === 0}
          onClick={() => setConfirm(true)}
          style={{ background: sel.size ? "#DC2626" : undefined, color: sel.size ? "#fff" : undefined }}
          data-testid="r2-delete-selected-btn"
        >
          <Trash2 size={15} /> Supprimer la sélection ({sel.size})
        </Button>
        {selectable.length > 0 && (
          <button
            className="text-xs underline"
            style={{ color: "var(--yalola-text-muted)" }}
            onClick={() => setSel((s) => (s.size === selectable.length ? new Set() : new Set(selectable.map((v) => v.path))))}
            data-testid="r2-select-all-btn"
          >
            {sel.size === selectable.length ? "Tout désélectionner" : "Tout sélectionner (non utilisées)"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="animate-spin mx-auto" style={{ color: "#0369A1" }} /></div>
      ) : videos.length === 0 ? (
        <div className="rounded-2xl border p-6 text-center text-sm" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text-muted)" }} data-testid="r2-empty">
          Aucun fichier vidéo sur R2.
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--yalola-border)", background: "#fff" }} data-testid="r2-video-list">
          {pager.slice.map((v) => {
            const checked = sel.has(v.path);
            return (
              <div key={v.path} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`r2-video-${v.path}`}>
                {v.referenced
                  ? <Lock size={14} className="shrink-0" style={{ color: "#047857" }} />
                  : <Checkbox checked={checked} onCheckedChange={() => toggle(v.path)} data-testid={`r2-check-${v.path}`} />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" title={v.path}>{fileName(v.path)}</div>
                  <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                    {v.owner ? `${v.owner} · ` : ""}{fmtDate(v.created)}
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0"
                  style={v.referenced ? { background: "rgba(16,185,129,0.15)", color: "#047857" } : { background: "rgba(180,83,9,0.15)", color: "#B45309" }}>
                  {v.referenced ? "Utilisée" : "Non utilisée"}
                </span>
                <span className="text-xs font-semibold shrink-0 w-20 text-right">{fmtBytes(v.size)}</span>
              </div>
            );
          })}
        </div>
      )}
      {!loading && <PagerBar page={pager.page} pages={pager.pages} total={pager.total} shown={pager.slice.length} onPage={pager.setPage} noun="fichier" testId="r2-pager" />}

      <Dialog open={confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(false); }}>
        <DialogContent data-testid="r2-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer {sel.size} fichier{sel.size > 1 ? "s" : ""} R2 ?</DialogTitle>
            <DialogDescription>
              Action définitive. Les fichiers encore utilisés par une publication seront automatiquement ignorés.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)} disabled={busy} data-testid="r2-confirm-cancel">Annuler</Button>
            <Button onClick={runCleanup} disabled={busy} style={{ background: "#DC2626", color: "#fff" }} data-testid="r2-confirm-delete">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default R2StorageSection;
